import React, { useRef, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.solteros.it/speeddate/api';

const TOOLBAR = [
  { cmd: 'bold', icon: 'fas fa-bold', title: 'Grassetto' },
  { cmd: 'italic', icon: 'fas fa-italic', title: 'Corsivo' },
  { cmd: 'underline', icon: 'fas fa-underline', title: 'Sottolineato' },
  { type: 'sep' },
  { cmd: 'formatBlock', val: 'h2', icon: 'fas fa-heading', title: 'Titolo' },
  { cmd: 'formatBlock', val: 'h3', icon: 'fas fa-heading', val2: 'h3', title: 'Sottotitolo' },
  { cmd: 'formatBlock', val: 'p', icon: 'fas fa-paragraph', title: 'Paragrafo' },
  { type: 'sep' },
  { cmd: 'insertUnorderedList', icon: 'fas fa-list-ul', title: 'Elenco puntato' },
  { cmd: 'insertOrderedList', icon: 'fas fa-list-ol', title: 'Elenco numerato' },
  { type: 'sep' },
  { cmd: 'insertHorizontalRule', icon: 'fas fa-minus', title: 'Linea orizzontale' },
  { type: 'sep' },
  { cmd: 'createLink', icon: 'fas fa-link', title: 'Inserisci link' },
  { cmd: 'insertImage', icon: 'fas fa-image', title: 'Carica immagine', upload: true },
  { type: 'sep' },
  { cmd: 'removeFormat', icon: 'fas fa-eraser', title: 'Rimuovi formattazione' },
];

export default function HtmlEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [htmlMode, setHtmlMode] = useState(false);
  const [rawHtml, setRawHtml] = useState(value || '');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (editorRef.current && !htmlMode) {
      const currentHtml = editorRef.current.innerHTML;
      const newVal = value || '';
      if (currentHtml !== newVal) {
        const sel = window.getSelection();
        const saved = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
        const savedOffset = saved ? saved.startOffset : 0;
        const savedNode = saved ? saved.startContainer : null;

        editorRef.current.innerHTML = newVal;

        if (savedNode && editorRef.current.contains(savedNode)) {
          try {
            const range = document.createRange();
            range.setStart(savedNode, Math.min(savedOffset, savedNode.textContent?.length || 0));
            range.collapse(true);
            sel?.removeAllRanges();
            sel?.addRange(range);
          } catch (e) {}
        }
      }
    }
  }, [value, htmlMode]);

  const exec = useCallback((cmd, val) => {
    document.execCommand(cmd, false, val || null);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    editorRef.current?.focus();
  }, [onChange]);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API_URL}/cover-images`, formData, {
        headers: { 'x-admin-token': token, 'Content-Type': 'multipart/form-data' }
      });
      // Insert the data URI (base64) directly so the image persists in the DB
      const data = res.data.data || res.data.url;
      const url = data.startsWith('data:')
        ? data
        : (data.startsWith('http') ? data : `${window.location.origin}${data}`);
      document.execCommand('insertImage', false, url);
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
      editorRef.current?.focus();
      window.Swal.fire({ title: 'Immagine inserita!', icon: 'success', timer: 1200, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) {
      window.Swal.fire('Errore', 'Errore caricamento immagine', 'error');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  }, [onChange]);

  const handleToolbarClick = useCallback((item) => {
    if (item.cmd === 'createLink') {
      const url = prompt('Inserisci URL:', 'https://');
      if (url) exec(item.cmd, url);
    } else if (item.upload) {
      fileInputRef.current?.click();
    } else {
      exec(item.cmd, item.val);
    }
  }, [exec]);

  const handleEditorInput = useCallback(() => {
    if (editorRef.current && !htmlMode) {
      const html = editorRef.current.innerHTML;
      onChange(html);
    }
  }, [onChange, htmlMode]);

  const handleRawChange = useCallback((e) => {
    const html = e.target.value;
    setRawHtml(html);
    onChange(html);
  }, [onChange]);

  return (
    <div className="border rounded-3 overflow-hidden" style={{ borderColor: '#dee2e6' }}>
      {/* Toolbar */}
      <div className="d-flex flex-wrap align-items-center gap-1 p-2 bg-light border-bottom" style={{ minHeight: 44 }}>
        {TOOLBAR.map((item, i) =>
          item.type === 'sep' ? (
            <div key={i} className="border-start mx-1" style={{ height: 24 }}></div>
          ) : (
            <button
              key={i}
              type="button"
              className="btn btn-sm btn-light border-0 rounded px-2"
              onClick={() => handleToolbarClick(item)}
              title={item.title}
              style={{ fontSize: 13 }}
              disabled={htmlMode || (item.upload && uploadingImage)}
            >
              {item.upload && uploadingImage ? (
                <span className="spinner-border spinner-border-sm" role="status"></span>
              ) : (
                <i className={item.icon}></i>
              )}
              {item.val === 'h2' && <span className="ms-1 small fw-bold" style={{ fontSize: 10 }}>H2</span>}
              {item.val === 'h3' && <span className="ms-1 small fw-bold" style={{ fontSize: 10 }}>H3</span>}
            </button>
          )
        )}
        <div className="ms-auto d-flex align-items-center gap-2">
          <span className={`small ${htmlMode ? 'text-primary fw-bold' : 'text-muted'}`}>HTML</span>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="htmlEditorToggle"
              checked={htmlMode}
              onChange={e => setHtmlMode(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </div>
      </div>

      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        className="d-none"
        onChange={handleImageUpload}
      />

      {/* Editor area */}
      {htmlMode ? (
        <textarea
          className="form-control border-0 rounded-0"
          style={{ minHeight: 300, fontFamily: "'Courier New', monospace", fontSize: 13, resize: 'vertical' }}
          value={rawHtml}
          onChange={handleRawChange}
        />
      ) : (
        <div
          ref={editorRef}
          className="form-control border-0 rounded-0 overflow-auto"
          contentEditable
          suppressContentEditableWarning
          onInput={handleEditorInput}
          dir="ltr"
          style={{ minHeight: 300, maxHeight: '60vh', lineHeight: 1.6, cursor: 'text', textAlign: 'left' }}
          data-placeholder="Scrivi il contenuto dell'articolo..."
        />
      )}

      {/* Status bar */}
      <div className="d-flex justify-content-between align-items-center px-3 py-1 bg-light border-top small text-muted">
        <span>{htmlMode ? 'Modalità HTML' : 'Modalità visiva'}</span>
        <span>{rawHtml ? `${rawHtml.length} caratteri` : 'vuoto'}</span>
      </div>

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #adb5bd;
          pointer-events: none;
        }
        [contenteditable]:focus {
          outline: none;
        }
        [contenteditable] {
          direction: ltr;
          text-align: left;
        }
        [contenteditable] img {
          max-width: 100%;
          border-radius: 8px;
          margin: 8px 0;
        }
        [contenteditable] a {
          color: #6366f1;
        }
        [contenteditable] h2, [contenteditable] h3 {
          margin-top: 16px;
          margin-bottom: 8px;
        }
        [contenteditable] ul, [contenteditable] ol {
          padding-left: 24px;
        }
        [contenteditable] hr {
          border: none;
          border-top: 2px solid #dee2e6;
          margin: 16px 0;
        }
      `}</style>
    </div>
  );
}